import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Col, Form, Row, Table } from '@edgebox/react-components';
import { useNavigate } from 'react-router-dom';
import { IPagedListResponse } from '@edgebox/data-definition-kit';
import { AccessScope, AiPromptTemplateStatus } from '@edgebox/sync-core-data-definitions';
import { ClientAiPromptTemplateEntity } from '@edgebox/sync-core-rest-client';
import { AccessScopeFilter, enumValues, normalizeFilterId, StatusFilter, useSyncCoreApi } from '../aiHelpers';
import { AiModelName } from '../components/AiModelName';
import { AiProviderName } from '../components/AiProviderName';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { ScreenHeader } from '../components/ScreenHeader';

export function AiPromptTemplatesView() {
  const api = useSyncCoreApi();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<StatusFilter<AiPromptTemplateStatus>>('Default');
  const [accessScope, setAccessScope] = useState<AccessScopeFilter>('Any');
  const [providerId, setProviderId] = useState<string>('');
  const [modelId, setModelId] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [keyFilter, setKeyFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [response, setResponse] = useState<IPagedListResponse<ClientAiPromptTemplateEntity> | undefined>(undefined);

  const filters = useMemo(() => {
    return {
      status: statusFilter === 'Default' ? [AiPromptTemplateStatus.Active] : statusFilter,
      accessScope: accessScope === 'Any' ? undefined : accessScope,
      providerId: normalizeFilterId(providerId),
      modelId: normalizeFilterId(modelId),
      customerId: normalizeFilterId(customerId),
      projectId: normalizeFilterId(projectId),
      key: keyFilter.trim().length ? keyFilter.trim() : undefined,
      search: search.trim().length ? search.trim() : undefined,
    };
  }, [statusFilter, accessScope, providerId, modelId, customerId, projectId, keyFilter, search]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.promptTemplates
      .list({ page: 0, itemsPerPage: 50 }, filters)
      .then((res) => {
        if (cancelled) return;
        setResponse(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, filters]);

  const items = response?.items ?? [];

  return (
    <>
      <ScreenHeader
        title="Prompt templates"
        right={
          <Button variant="primary" onClick={() => navigate('/ai/prompt-template/new')}>
            Add template
          </Button>
        }
      />

      <Card className="mb-3">
        <CardBody>
          <Form>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter === 'Default' ? 'Default' : 'Custom'}
                    onChange={(e) => setStatusFilter(e.target.value === 'Default' ? 'Default' : [])}
                  >
                    <option value="Default">Default (Active)</option>
                    <option value="Custom">Custom</option>
                  </Form.Select>
                </Form.Group>

                {statusFilter !== 'Default' && (
                  <div className="mt-2">
                    {enumValues(AiPromptTemplateStatus).map((s) => (
                      <Form.Check
                        key={s}
                        type="checkbox"
                        label={s}
                        checked={(statusFilter as AiPromptTemplateStatus[]).includes(s)}
                        onChange={(e) => {
                          const current = statusFilter as AiPromptTemplateStatus[];
                          setStatusFilter(e.target.checked ? [...current, s] : current.filter((x) => x !== s));
                        }}
                      />
                    ))}
                  </div>
                )}
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Access scope</Form.Label>
                  <Form.Select value={accessScope} onChange={(e) => setAccessScope(e.target.value as AccessScopeFilter)}>
                    <option value="Any">Any</option>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Provider ID</Form.Label>
                  <Form.Control value={providerId} onChange={(e) => setProviderId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Model ID</Form.Label>
                  <Form.Control value={modelId} onChange={(e) => setModelId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Key</Form.Label>
                  <Form.Control value={keyFilter} onChange={(e) => setKeyFilter(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Search</Form.Label>
                  <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Customer ID</Form.Label>
                  <Form.Control value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Project ID</Form.Label>
                  <Form.Control value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {loading ? <LoadingCard text="Loading templates…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      <Card>
        <CardHeader>
          Templates <Badge bg="secondary">{response?.totalNumberOfItems ?? items.length}</Badge>
        </CardHeader>
        <CardBody>
          <Table striped bordered hover size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Status</th>
                <th>Scope</th>
                <th>Provider</th>
                <th>Model</th>
                <th>Frozen</th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="text-monospace">{t.key ?? '-'}</td>
                  <td>
                    <Badge
                      bg={
                        t.status === AiPromptTemplateStatus.Active
                          ? 'success'
                          : t.status === AiPromptTemplateStatus.Draft
                            ? 'warning'
                            : 'secondary'
                      }
                    >
                      {t.status}
                    </Badge>
                  </td>
                  <td>{t.accessScope}</td>
                  <td>
                    <AiProviderName id={t.provider?.getId?.()} />
                  </td>
                  <td>
                    <AiModelName id={t.model?.getId?.()} />
                  </td>
                  <td>{t.frozen ? 'Yes' : 'No'}</td>
                  <td>
                    <Button size="sm" variant="light" onClick={() => navigate(`/ai/prompt-template/${t.id}`)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={8} className="text-muted">
                    No templates found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
}
